import { useRef, type PointerEvent } from 'react';
import { useLongPress } from './useLongPress';
import styles from './Pad.module.css';

export interface PadProps {
  /** MPC pad ID, A01–A16 */
  padId: string;
  /** Role name shown under the ID, e.g. "Kick" */
  label?: string;
  hasSample?: boolean;
  selected?: boolean;
  lit?: boolean;
  disabled?: boolean;
  /** Tap = play and select */
  onPress?: () => void;
  /** Shortcut for Edit pad */
  onLongPress?: () => void;
  /**
   * Plays and selects on pointerdown, like an MPC pad (DESIGN_SYSTEM §5: lit within 120 ms).
   * A touch on a screen that can scroll strikes on release instead, and not at all if it
   * moved or was held for a long-press, so a scroll or Edit pad never makes a stray hit.
   */
  onStrike?: () => void;
}

const SLOP = 10;
const HOLD_MS = 500;

function pageScrolls(el: HTMLElement): boolean {
  const main = el.closest('#main') ?? document.scrollingElement;
  return !!main && main.scrollHeight > main.clientHeight + 1;
}


/** MPC-style pad (DESIGN_SYSTEM §3). Announces "A01, kick, lit". */
export function Pad({ padId, label, hasSample, selected, lit, disabled, onPress, onLongPress, onStrike }: PadProps) {
  const press = useLongPress(onLongPress, onStrike ? undefined : onPress);
  const hit = useRef<{ x: number; y: number; at: number } | null>(null);
  const name = [padId, label?.toLowerCase() ?? 'empty', lit && 'lit'].filter(Boolean).join(', ');
  const classes = [styles.pad, selected && styles.selected, lit && styles.lit].filter(Boolean).join(' ');
  const strike = onStrike
    ? {
        onPointerDown: (e: PointerEvent<HTMLButtonElement>) => {
          press.onPointerDown(e);
          if (e.pointerType === 'touch' && pageScrolls(e.currentTarget)) hit.current = { x: e.clientX, y: e.clientY, at: performance.now() };
          else onStrike();
        },
        onPointerMove: (e: PointerEvent<HTMLButtonElement>) => {
          press.onPointerMove(e);
          if (hit.current && Math.hypot(e.clientX - hit.current.x, e.clientY - hit.current.y) > SLOP) hit.current = null;
        },
        onPointerUp: () => {
          press.onPointerUp();
          const h = hit.current;
          hit.current = null;
          if (h && performance.now() - h.at < HOLD_MS) onStrike();
        },
        onPointerCancel: () => {
          press.onPointerCancel();
          hit.current = null;
        },
        onClick: (e: { detail: number }) => {
          if (e.detail === 0) onStrike(); // keyboard
        },
      }
    : {};
  return (
    <button type="button" className={classes} aria-label={name} aria-pressed={selected} disabled={disabled} {...press} {...strike}>
      {hasSample && <span className={styles.dot} />}
      <span className={styles.id} aria-hidden="true">{padId}</span>
      {label && <span className={styles.role} aria-hidden="true">{label}</span>}
    </button>
  );
}

/**
 * Small pad at the start of a Library row: tap to audition. `onPointerDown`
 * is a separate, earlier hook for unlocking audio (DESIGN_SYSTEM §5): it must
 * run synchronously in the gesture itself, before the full tap (`onPress`,
 * on release) completes, and before any awaited sample load.
 */
export function MiniPad({ label, lit, glyph, onPress, onPointerDown }: { label: string; lit?: boolean; glyph?: boolean; onPress?: () => void; onPointerDown?: () => void }) {
  // `glyph`: a ▶ so the row's audition control is told apart by more than its border (audit A7, Library rows only;
  // the kit pads mirror the MPC pads and stay bare, DESIGN_SYSTEM §3). Drawn in CSS: the Library renders 500 rows, and
  // an SVG per row cost the Select all budget on WebKit.
  const classes = [styles.pad, styles.mini, lit && styles.lit, glyph && styles.glyph].filter(Boolean).join(' ');
  return <button type="button" className={classes} aria-label={label} onClick={onPress} onPointerDown={onPointerDown} />;
}
